/**
 * « Signaler un bug » — collecte de contexte, validation et envoi.
 *
 * Ce fichier reprend, volontairement à l'identique, le contrat de
 * `ouitap-haccp/src/lib/bugreport/core.ts` (écrit en parallèle pour le
 * pupitre HACCP) : mêmes champs, même ring buffer d'erreurs client, même
 * découpage validation / contexte / envoi. Les deux copies existent parce
 * que les deux applications ne partagent aucun code entre elles — pas parce
 * que le contrat aurait divergé. Le contrat de fil (noms de champs,
 * tailles, codes d'erreur) est fixé côté serveur ; on s'y conforme à la
 * lettre plutôt que d'improviser une variante.
 */

import { currentBuildVersion } from '@/app/buildVersion';

export type BugReportSeverity = 'blocking' | 'annoying' | 'minor';

export const SEVERITIES: readonly BugReportSeverity[] = ['blocking', 'annoying', 'minor'];

export const MAX_IMAGES = 5;
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
export const MAX_VIDEO_SECONDS = 60;
export const MIN_DESCRIPTION_LENGTH = 3;
export const MAX_DESCRIPTION_LENGTH = 4000;
export const IMAGE_MAX_EDGE = 1600;
export const IMAGE_JPEG_QUALITY = 0.8;

const MAX_RECENT_ERRORS = 10;
const MAX_ERROR_MESSAGE_LENGTH = 300;

export interface RecentErrorEntry {
  at: string;
  message: string;
}

/**
 * Les clés sont en snake_case à dessein : cet objet part tel quel, sérialisé
 * en JSON, dans le champ `context` du multipart — le contrat de fil fixé par
 * le serveur, pas une convention interne de ce module.
 */
export interface BugReportContext {
  app_version: string;
  route: string;
  lang: string;
  online: boolean;
  user_agent: string;
  screen: string;
  viewport: string;
  pixel_ratio: number;
  standalone: boolean;
  recent_errors: RecentErrorEntry[];
}

export interface BugReportDraft {
  severity: BugReportSeverity | null;
  description: string;
  images: File[];
  video: File | null;
}

export type BugReportFieldError =
  | 'severityRequired'
  | 'descriptionRequired'
  | 'descriptionTooLong'
  | 'tooManyImages'
  | 'imageTooLarge'
  | 'videoTooLarge'
  | 'videoTooLong';

/** Validation client — un filet avant l'envoi, le serveur revalide tout. */
export function validateDraft(draft: BugReportDraft): BugReportFieldError[] {
  const errors: BugReportFieldError[] = [];
  if (!draft.severity) errors.push('severityRequired');
  const trimmed = draft.description.trim();
  if (trimmed.length < MIN_DESCRIPTION_LENGTH) errors.push('descriptionRequired');
  else if (trimmed.length > MAX_DESCRIPTION_LENGTH) errors.push('descriptionTooLong');
  if (draft.images.length > MAX_IMAGES) errors.push('tooManyImages');
  if (draft.images.some((image) => image.size > MAX_IMAGE_BYTES)) errors.push('imageTooLarge');
  if (draft.video && draft.video.size > MAX_VIDEO_BYTES) errors.push('videoTooLarge');
  return errors;
}

function randomId(): string {
  // crypto.randomUUID demande un contexte sécurisé ; repli pour le dev en
  // http nu (même filet que src/utils/clientId.ts).
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `bugreport-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Un `op_id` par ouverture de la feuille — les tentatives suivantes (après
 *  une erreur) réutilisent le même, pour que l'idempotence côté serveur
 *  évite un doublon si l'envoi précédent a en fait abouti. */
export function createOpId(): string {
  return randomId();
}

function isStandaloneDisplay(): boolean {
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch {
    // matchMedia absent : repli sur le drapeau iOS ci-dessous.
  }
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** Contexte technique auto-collecté, joint à chaque envoi. `lang` est passé
 *  par l'appelant (celui qui tient `useTranslation()`) plutôt que lu ici
 *  depuis le singleton i18n, pour garder ce module sans dépendance sur
 *  react-i18next. */
export function collectContext(lang: string): BugReportContext {
  return {
    app_version: currentBuildVersion(),
    route: window.location.pathname,
    lang,
    online: navigator.onLine,
    user_agent: navigator.userAgent,
    screen: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    pixel_ratio: window.devicePixelRatio || 1,
    standalone: isStandaloneDisplay(),
    recent_errors: recentErrorsSnapshot(),
  };
}

// ---------------------------------------------------------------------------
// Ring buffer d'erreurs client — installé une seule fois, au démarrage de
// l'appli serveur (WaiterShell), pour que le rapport de bug parte avec ce
// qui a précédé la remontée sans que le serveur en collecte la moindre
// miette avant qu'un rapport ne soit réellement envoyé.
// ---------------------------------------------------------------------------

let recentErrors: RecentErrorEntry[] = [];
let ringBufferInstalled = false;

function pushRecentError(rawMessage: unknown): void {
  const message = String(rawMessage ?? '').slice(0, MAX_ERROR_MESSAGE_LENGTH);
  if (!message) return;
  recentErrors = [...recentErrors, { at: new Date().toISOString(), message }].slice(-MAX_RECENT_ERRORS);
}

function onWindowError(event: ErrorEvent): void {
  pushRecentError(event.message || event.error?.message);
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason: unknown = event.reason;
  pushRecentError(reason instanceof Error ? reason.message : reason);
}

export function installErrorRingBuffer(): void {
  if (ringBufferInstalled) return;
  ringBufferInstalled = true;
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
}

/** Réservé aux tests : l'installation ci-dessus est un singleton de module,
 *  qui survit sinon d'un test à l'autre. */
export function resetErrorRingBufferForTests(): void {
  recentErrors = [];
  ringBufferInstalled = false;
  window.removeEventListener('error', onWindowError);
  window.removeEventListener('unhandledrejection', onUnhandledRejection);
}

export function recentErrorsSnapshot(): RecentErrorEntry[] {
  return recentErrors;
}

// ---------------------------------------------------------------------------
// Photo / vidéo — redimensionnement client (confort, pas condition d'envoi :
// le serveur retraite de toute façon) et lecture de durée vidéo.
// ---------------------------------------------------------------------------

function jpegFileName(originalName: string): string {
  const base = originalName.replace(/\.[^./\\]+$/, '');
  return `${base || 'photo'}.jpg`;
}

/** Redimensionne une photo au plus grand côté `maxEdge`, ré-encodée en JPEG.
 *  `createImageBitmap` décode le `File` directement, sans passer par une URL
 *  d'objet ni un `<img>` qui attend un évènement — un navigateur qui ne
 *  l'implémente pas (ou un format qu'il ne sait pas décoder) renvoie
 *  immédiatement le fichier tel quel plutôt que d'attendre indéfiniment un
 *  `load` qui ne viendra jamais. Le serveur réencode de toute façon en WEBP
 *  côté haccp, et fera de même ici — un renvoi non redimensionné n'est
 *  jamais un échec d'envoi. */
export async function resizeImageForUpload(
  file: File,
  maxEdge: number = IMAGE_MAX_EDGE,
  quality: number = IMAGE_JPEG_QUALITY,
): Promise<File> {
  if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  try {
    const largestEdge = Math.max(bitmap.width, bitmap.height);
    if (!largestEdge || largestEdge <= maxEdge) return file;
    const scale = maxEdge / largestEdge;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return file;
    return new File([blob], jpegFileName(file.name), { type: 'image/jpeg' });
  } finally {
    bitmap.close();
  }
}

const VIDEO_METADATA_TIMEOUT_MS = 8000;

/** Durée d'une vidéo en secondes, lue via un `<video>` hors DOM. `null` si
 *  elle n'a pas pu être déterminée (métadonnées illisibles, ou ni `load` ni
 *  `error` ne se déclenchent avant `VIDEO_METADATA_TIMEOUT_MS` — un format
 *  que le navigateur accepte de garder en attente plutôt que de refuser
 *  franchement) — appelant responsable de décider si ça bloque l'envoi ou
 *  non. */
export function readVideoDurationSeconds(file: File): Promise<number | null> {
  const objectUrl = URL.createObjectURL(file);
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const finish = (value: number | null) => {
      window.clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
      resolve(value);
    };
    const timer = window.setTimeout(() => finish(null), VIDEO_METADATA_TIMEOUT_MS);
    video.preload = 'metadata';
    video.onloadedmetadata = () => finish(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => finish(null);
    video.src = objectUrl;
  });
}

// ---------------------------------------------------------------------------
// Envoi — XHR plutôt que fetch : c'est le seul des deux à exposer une
// progression d'upload (`upload.onprogress`), utile dès qu'une vidéo de
// plusieurs dizaines de Mo est jointe sur un réseau de restaurant.
// ---------------------------------------------------------------------------

export interface UploadBugReportSuccess {
  uuid: string;
  reference: string;
  createdAt: string;
}

export type UploadBugReportError =
  | { kind: 'invalid'; fields?: Record<string, unknown> }
  | { kind: 'tooLarge' }
  | { kind: 'throttled' }
  | { kind: 'network' }
  | { kind: 'unknown'; status: number };

export type UploadBugReportResult =
  | { ok: true; data: UploadBugReportSuccess }
  | { ok: false; error: UploadBugReportError };

export interface UploadBugReportArgs {
  /** Origine seule (pas de chemin) — même filet que `saleApi.ts`. */
  baseUrl: string;
  token: string;
  opId: string;
  severity: BugReportSeverity;
  description: string;
  context: BugReportContext;
  images: File[];
  video: File | null;
  onProgress?: (fraction: number) => void;
  /** Point d'injection pour les tests (XHR factice) ; `XMLHttpRequest` en
   *  conditions réelles. */
  xhrFactory?: () => XMLHttpRequest;
}

export function uploadBugReport(args: UploadBugReportArgs): Promise<UploadBugReportResult> {
  const xhr = args.xhrFactory ? args.xhrFactory() : new XMLHttpRequest();
  const form = new FormData();
  form.append('op_id', args.opId);
  form.append('severity', args.severity);
  form.append('description', args.description.trim());
  form.append('context', JSON.stringify(args.context));
  for (const image of args.images) form.append('images', image);
  if (args.video) form.append('video', args.video);

  return new Promise((resolve) => {
    xhr.open('POST', `${args.baseUrl}/sale/order/waiter-mode/bug-reports/`);
    xhr.setRequestHeader('Authorization', `Bearer ${args.token}`);
    if (xhr.upload && args.onProgress) {
      const onProgress = args.onProgress;
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      };
    }
    xhr.onerror = () => resolve({ ok: false, error: { kind: 'network' } });
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      const parsed = body as { uuid?: string; reference?: string; created_at?: string; code?: string; fields?: Record<string, unknown> } | null;

      if (xhr.status === 200 || xhr.status === 201) {
        if (parsed?.uuid && parsed.reference) {
          resolve({
            ok: true,
            data: { uuid: parsed.uuid, reference: parsed.reference, createdAt: parsed.created_at ?? '' },
          });
        } else {
          resolve({ ok: false, error: { kind: 'unknown', status: xhr.status } });
        }
        return;
      }
      if (xhr.status === 400 || parsed?.code === 'BUG_REPORT_INVALID') {
        resolve({ ok: false, error: { kind: 'invalid', fields: parsed?.fields } });
      } else if (xhr.status === 413 || parsed?.code === 'BUG_REPORT_TOO_LARGE') {
        resolve({ ok: false, error: { kind: 'tooLarge' } });
      } else if (xhr.status === 429) {
        resolve({ ok: false, error: { kind: 'throttled' } });
      } else {
        resolve({ ok: false, error: { kind: 'unknown', status: xhr.status } });
      }
    };
    xhr.send(form);
  });
}
